import type { ApolloClient } from '@apollo/client';

import { API_KEYS, CREATE_API_KEY, REGENERATE_API_KEY, type ApiKey } from '@/account/api-keys';
import { errorCode } from '@/account/arcade-api';

import { V1Error } from './api';

/**
 * The key the demo submits jobs with. The portal creates an API key named
 * "Demo" the first time it needs one and keeps its secret in this browser;
 * a device without the secret regenerates that same key rather than taking
 * another slot.
 */

export const DEMO_KEY_NAME = 'Demo';

interface Stored {
  keyId: string;
  secret: string;
}

export class DemoKeyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DemoKeyError';
  }
}

function storageKey(personId: string): string {
  return `lw.papermill.demoKey.${personId}`;
}

function read(personId: string): Stored | null {
  try {
    const raw = localStorage.getItem(storageKey(personId));
    return raw ? (JSON.parse(raw) as Stored) : null;
  } catch {
    return null;
  }
}

function write(personId: string, stored: Stored): void {
  try {
    localStorage.setItem(storageKey(personId), JSON.stringify(stored));
  } catch {
    // Without storage the secret lives for this call only; the next job regenerates.
  }
}

export function forgetDemoKey(personId: string): void {
  try {
    localStorage.removeItem(storageKey(personId));
  } catch {
    // Nothing to forget.
  }
}

async function mint(client: ApolloClient, personId: string, keys: ApiKey[]): Promise<string> {
  const existing = keys.find((key) => !key.revokedAt && key.name === DEMO_KEY_NAME);
  try {
    const { key, secret } = existing
      ? (await client.mutate({ mutation: REGENERATE_API_KEY, variables: { id: existing.id } }))
          .data!.regenerateApiKey
      : (await client.mutate({ mutation: CREATE_API_KEY, variables: { name: DEMO_KEY_NAME } }))
          .data!.createApiKey;
    write(personId, { keyId: key.id, secret });
    void client.refetchQueries({ include: [API_KEYS] });
    return secret;
  } catch (error) {
    if (errorCode(error) === 'TOO_MANY_KEYS') {
      throw new DemoKeyError(
        'The demo needs an API key of its own, and you already have the most active keys allowed. Revoke one under Settings → API Keys and try again.',
      );
    }
    throw error;
  }
}

/** The demo key's secret, creating or regenerating the key when this browser has none. */
export async function demoSecret(
  client: ApolloClient,
  personId: string,
  { refresh = false } = {},
): Promise<string> {
  const { data } = await client.query({ query: API_KEYS, fetchPolicy: 'no-cache' });
  const keys = data?.apiKeys ?? [];
  const stored = refresh ? null : read(personId);
  const key = stored && keys.find((candidate) => candidate.id === stored.keyId);
  if (
    stored &&
    key &&
    !key.revokedAt &&
    key.secretPrefix &&
    stored.secret.startsWith(key.secretPrefix)
  ) {
    return stored.secret;
  }
  return mint(client, personId, keys);
}

/** Run `call` with the demo secret, minting a fresh one once if Arcade refuses it. */
export async function withDemoKey<T>(
  client: ApolloClient,
  personId: string,
  call: (secret: string) => Promise<T>,
): Promise<T> {
  try {
    return await call(await demoSecret(client, personId));
  } catch (error) {
    if (error instanceof V1Error && error.status === 401) {
      forgetDemoKey(personId);
      return call(await demoSecret(client, personId, { refresh: true }));
    }
    throw error;
  }
}
